// API calls to the fulfilment-call-service (STSMPD-1322).
//
// The call service is a separate origin (not flex-core's /api/v2), so we build the Request
// against its configurable base URL — `REACT_APP_FULFILMENT_CALL_SERVICE_URL` — rather than
// via apiURL(). We still reuse `handleCall` from syft-acp-core/api/call for consistent
// response unpacking + error handling.

import { handleCall, getAuthHeaders } from 'syft-acp-core/api/call'
import config from 'syft-acp-core/config'

import { CreateCallRequestPayload, CreateCallResponse, CallRequestView } from './types'

/** v1 scenario shipped for the Phase 1 PoC ("late-notice fill check"). */
export const DEFAULT_SCRIPT_ID = 'late_notice_fill_check_v1'

/** Local dev/test convenience target. NEVER used in a deployed (production-built) bundle. */
const LOCAL_DEV_BASE_URL = 'http://localhost:8080'

const isNonEmptyString = (value: unknown): boolean => typeof value === 'string' && value.trim() !== ''

/**
 * True when the call service is fully configured to place a call AND record its outcome.
 *
 * Requires BOTH the service base URL and the Slack channel: the call request contract treats
 * `REACT_APP_FULFILMENT_CALL_SLACK_CHANNEL` as required (the service posts the durable outcome/alert
 * to Slack), so a deploy with only the URL set would place calls whose outcome can't be delivered.
 * The action is gated on this so we never place authenticated calls against a half-configured deploy.
 */
export const isCallServiceConfigured = (): boolean =>
  isNonEmptyString(config.fulfilmentCallServiceUrl) && isNonEmptyString(config.fulfilmentCallSlackChannel)

/**
 * Resolve the call-service base URL, failing closed.
 *
 * - Configured URL → used (trailing slash trimmed).
 * - No URL + non-production build (`NODE_ENV` dev/test) → localhost convenience fallback.
 * - No URL + production build (staging/preprod/prod all ship `NODE_ENV=production`) → throw, so we
 *   never send authenticated requests to localhost off the back of a misconfigured deploy.
 *
 * NB: we key the fallback off the BUILD env (`process.env.NODE_ENV`), not `config.isDeployEnvTestOrDev()`
 * — the latter is true for staging, where localhost must still be off-limits.
 */
const resolveBaseUrl = (): string => {
  const url = config.fulfilmentCallServiceUrl
  if (typeof url === 'string' && url.trim() !== '') {
    return url.trim().replace(/\/?$/, '')
  }
  if (process.env.NODE_ENV !== 'production') {
    return LOCAL_DEV_BASE_URL
  }
  throw new Error(
    'Fulfilment call service URL is not configured (REACT_APP_FULFILMENT_CALL_SERVICE_URL). ' +
      'Refusing to place a call against an unconfigured endpoint.',
  )
}

const jsonRequest = (path: string, method: string, body?: Record<string, any>): Request => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(getAuthHeaders() as Record<string, string>),
  }
  return new Request(`${resolveBaseUrl()}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
}

// handleCall resolves with `{ payload, response, meta }`; callers here only want the payload.
const unwrap =
  <T>(resolve: (value: T) => void) =>
  (result: { payload: T }) =>
    resolve(result.payload)

/** POST /call-requests — create a call request (and place the call if enabled). */
export const createCallRequest = (payload: CreateCallRequestPayload): Promise<CreateCallResponse> =>
  new Promise((resolve, reject) => {
    handleCall(jsonRequest('/call-requests', 'POST', payload), unwrap<CreateCallResponse>(resolve), reject)
  })

/** GET /call-requests/:id — poll the current state of a call request. */
export const getCallRequest = (callRequestId: string): Promise<CallRequestView> =>
  new Promise((resolve, reject) => {
    handleCall(
      jsonRequest(`/call-requests/${callRequestId}`, 'GET'),
      unwrap<CallRequestView>(resolve),
      reject,
    )
  })
